import type { UserAuth, UserProfile } from '@/types/initial-request';

function normalizeString(value: unknown) {
  if (typeof value !== 'string') return '';
  return value.trim();
}

function normalizeNameLike(value: unknown) {
  const normalized = normalizeString(value);
  if (!normalized) return '';
  if (normalized.toLowerCase() === 'not provided') return '';
  return normalized;
}

function normalizeNumber(value: unknown, fallback: number) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value === null || value === undefined) return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizePrimaryCourt(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 0 ? value : undefined;
  }
  if (value === null || value === undefined) return undefined;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return undefined;
  return parsed > 0 ? parsed : undefined;
}

export function normalizeUserProfile(profile?: UserProfile, userAuth?: UserAuth): UserProfile | undefined {
  const username = normalizeString(profile?.username) || normalizeString(userAuth?.username);
  if (!username) return undefined;

  return {
    username,
    name: normalizeNameLike(profile?.name) || normalizeNameLike(userAuth?.name),
    nickName: normalizeNameLike(profile?.nickName) || normalizeNameLike(userAuth?.nickName),
    email: normalizeString(profile?.email) || normalizeString(userAuth?.email),
    phoneNumber: normalizeString(profile?.phoneNumber) || normalizeString(userAuth?.phoneNumber),
    intCode: normalizeString(profile?.intCode),
    dateOfBirth: normalizeString(profile?.dateOfBirth),
    primaryCourt: normalizePrimaryCourt(profile?.primaryCourt),
    gender: normalizeNumber(profile?.gender, 0),
    country: normalizeString(profile?.country),
    city: normalizeString(profile?.city),
    state: normalizeString(profile?.state),
    zipcode: normalizeString(profile?.zipcode),
    statusGeneral: normalizeNumber(profile?.statusGeneral, 1),
    statusPreferredLevel: normalizeNumber(profile?.statusPreferredLevel, 1),
    statusMatchType: normalizeNumber(profile?.statusMatchType, 5),
    levelCompetition: normalizeNumber(profile?.levelCompetition, 1),
    bioDesc: normalizeString(profile?.bioDesc),
    privacyDefaultAudience: normalizeNumber(profile?.privacyDefaultAudience, 1),
    privacyFollower: normalizeNumber(profile?.privacyFollower, 1),
    profilePhoto: normalizeString(profile?.profilePhoto),
    selfRanking: normalizeNumber(profile?.selfRanking, 2),
    generalPickleballSkillLevel: normalizeNumber(profile?.generalPickleballSkillLevel, 1),
    duprID: normalizeString(profile?.duprID),
    duprFullName: normalizeString(profile?.duprFullName),
    duprFirstName: normalizeString(profile?.duprFirstName),
    duprLastName: normalizeString(profile?.duprLastName),
    duprEmail: normalizeString(profile?.duprEmail),
    duprRatingSingles: normalizeString(profile?.duprRatingSingles),
    duprIsSinglesReliable: Boolean(profile?.duprIsSinglesReliable),
    duprRatingDoubles: normalizeString(profile?.duprRatingDoubles),
    duprIsDoublesReliable: Boolean(profile?.duprIsDoublesReliable),
    emailVerified: Boolean(profile?.emailVerified),
    phoneVerified: Boolean(profile?.phoneVerified),
    guestAccount: Boolean(profile?.guestAccount),
  };
}
